import { BoxGeometry, EdgesGeometry } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { Cuboid } from '../data/types';

/**
 * One unit-box geometry (+ its edges), shared by every cuboid and scaled per
 * instance. 151 boxes → 2 geometries instead of 302.
 */
const UNIT_BOX = new BoxGeometry(1, 1, 1);
const UNIT_EDGES = new EdgesGeometry(UNIT_BOX);

const EDGE_COLOR = '#e6edf3';
const HOVER_COLOR = '#ffcf33';

/** What the app needs to position a tooltip and know which box is lit. */
export interface HoverInfo {
  cuboid: Cuboid;
  x: number; // client px (for the tooltip)
  y: number;
}

interface CuboidsProps {
  cuboids: Cuboid[];
  hoveredId: string | null;
  onHover: (info: HoverInfo | null) => void;
}

export function Cuboids({ cuboids, hoveredId, onHover }: CuboidsProps) {
  return (
    <group>
      {cuboids.map((c) => (
        <CuboidBox key={c.uuid} cuboid={c} hovered={c.uuid === hoveredId} onHover={onHover} />
      ))}
    </group>
  );
}

function CuboidBox({
  cuboid,
  hovered,
  onHover,
}: {
  cuboid: Cuboid;
  hovered: boolean;
  onHover: (info: HoverInfo | null) => void;
}) {
  const [px, py, pz] = cuboid.position;
  const [dx, dy, dz] = cuboid.dimensions;
  const color = hovered ? HOVER_COLOR : EDGE_COLOR;

  const emit = (e: ThreeEvent<PointerEvent>) =>
    onHover({ cuboid, x: e.nativeEvent.clientX, y: e.nativeEvent.clientY });

  const handleOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation(); // only the front-most box under the cursor wins
    emit(e);
    document.body.style.cursor = 'pointer';
  };
  const handleOut = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onHover(null);
    document.body.style.cursor = 'auto';
  };

  return (
    <group position={[px, py, pz]} rotation={[0, 0, cuboid.yaw]} scale={[dx, dy, dz]}>
      {/* Semi-transparent fill; depthWrite off so it never hides the points inside it. */}
      <mesh geometry={UNIT_BOX} onPointerOver={handleOver} onPointerMove={emit} onPointerOut={handleOut}>
        <meshBasicMaterial color={color} transparent opacity={hovered ? 0.2 : 0.06} depthWrite={false} />
      </mesh>
      {/* Solid, well-defined edges. */}
      <lineSegments geometry={UNIT_EDGES}>
        <lineBasicMaterial color={color} transparent opacity={hovered ? 1 : 0.85} />
      </lineSegments>
    </group>
  );
}
